import { useCallback, useEffect, useRef } from "react";
import { FC, KeyboardEvent, memo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./search-bar.module.css";
import { MainHeaderEvents } from "../../main-header.events";
import {
  Product,
  debounce,
  useAnalytics,
  normalizeUrl,
  getApiUrl,
  getEnvVar,
  StoreMetadata } from
"@commons-monorepo/index";
import { SearchBarEvents } from "./search-bar.events";
import { camelizeKeys } from "humps";

interface SearchBarProps {
  className?: string;
  storeMetadata: StoreMetadata;
  isMobile: boolean;
  onSearch?: (query: string) => void;
  valueToSearch: string;
  setValueToSearch: (query: string) => void;
  setProducts: (products?: Product[]) => void;
  setShowSearchBar: (value: boolean) => void;
}

const SearchBar: FC<SearchBarProps> = ({
  valueToSearch,
  setValueToSearch,
  setProducts,
  className,
  onSearch,
  setShowSearchBar,
  storeMetadata,
  isMobile
}) => {
  const { t } = useTranslation();
  const [logEvent] = useAnalytics({ storeMetadata });
  const inputRef = useRef<HTMLInputElement>(null);
  const partnerId = storeMetadata?.userNetwork?.urlSummer;

  const handleSearch = (): void => {
    logEvent(SearchBarEvents.SearchProduct);
    onSearch && onSearch(valueToSearch);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      logEvent(MainHeaderEvents.SelectShowAllResults, { source: "enter" });
      inputRef.current?.blur();
      handleSearch();
    }
  };

  const handleSearchChange = (value: string) => {
    setValueToSearch(value);
  };

  const deleteSearch = () => {
    setValueToSearch("");
    setProducts();
    if (!isMobile) setShowSearchBar(false);
  };

  const fetchProducts = (query: string) => {
    if (!!query) {
      const headers: HeadersInit = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "api-key-sumer": getEnvVar("PUBLIC_API_KEY") || "",
        "X-API-VERSION": "2"
      };
      fetch(
        `${getApiUrl()}/api/ms/orchestrator/catalogue/${normalizeUrl(
          partnerId
        )}?term=${query}`,
        {
          method: "GET",
          headers
        }
      ).
      then(async (r) => (camelizeKeys(await r.json()) as any)).
      then((r) => {
        setProducts(r.products);
      }).
      catch(() => {});
    }
  };

  const handleFetchProducts = useCallback(debounce(fetchProducts, 400), []);

  useEffect(() => {
    handleFetchProducts(valueToSearch);
  }, [valueToSearch]);

  return (
    <div className={`${styles.searchBarWrapper} ${className}`}>
      <div className={styles.searchInputBox}>
        <span className={`${styles.iconSearch} icon-search`} />
        <input
          ref={inputRef}
          type='text'
          aria-label='Buscador principal'
          className={styles.input}
          value={valueToSearch}
          placeholder={t("main_header.search_placeholder")}
          onKeyDown={handleKeyDown}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocusCapture={() => logEvent(MainHeaderEvents.SelectInputSearch)} />

        {!!valueToSearch &&
        <div onClick={deleteSearch} className={styles.searchButtonBar}>
            <span className='icon-close' />
          </div>}

      </div>
      <div className={styles.close} onClick={() => setShowSearchBar(false)}>
        Cerrar
      </div>
    </div>);

};

export default memo(SearchBar);